import React from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

// Language display names and flags
const languageInfo = {
  en: { name: 'EN', flag: '🇺🇸', fullName: 'English' },
  es: { name: 'ES', flag: '🇪🇸', fullName: 'Español' },
  fr: { name: 'FR', flag: '🇫🇷', fullName: 'Français' },
  de: { name: 'DE', flag: '🇩🇪', fullName: 'Deutsch' },
  it: { name: 'IT', flag: '🇮🇹', fullName: 'Italiano' },
  pt: { name: 'PT', flag: '🇵🇹', fullName: 'Português' },
  ja: { name: 'JA', flag: '🇯🇵', fullName: '日本語' },
  zh: { name: 'ZH', flag: '🇨🇳', fullName: '中文' },
  ru: { name: 'RU', flag: '🇷🇺', fullName: 'Русский' },
  hi: { name: 'HI', flag: '🇮🇳', fullName: 'हिन्दी' },
  el: { name: 'EL', flag: '🇬🇷', fullName: 'Ελληνικά' },
  sv: { name: 'SV', flag: '🇸🇪', fullName: 'Svenska' }
};

const LanguageSwitcher = () => {
  const { ipDetectedLanguage } = useLanguage();
  const { i18n } = useTranslation();
  
  // Current language from i18n directly
  const currentLanguage = i18n.language;
  
  // Determine the two languages to show: EN and the IP-detected local language
  // If local language is English or not detected, default to Spanish as second option
  const localLanguage = ipDetectedLanguage && ipDetectedLanguage !== 'en' 
    ? ipDetectedLanguage 
    : 'es'; // Default to Spanish if no local language or local is English
  
  // Always show two options: English and local language
  const availableLanguages = ['en', localLanguage];

  const handleLanguageToggle = async (langCode) => {
    try {
      await i18n.changeLanguage(langCode);
      console.log('Language switched to:', langCode);
    } catch (error) {
      console.error('Error switching language:', error);
    }
  };

  return (
    <div 
      className="flex items-center gap-1 bg-gray-800/60 backdrop-blur rounded-full px-2 py-1"
      data-testid="language-switcher"
    >
      <Globe className="w-3.5 h-3.5 text-gray-400 mr-1" />
      {availableLanguages.map((langCode, index) => {
        const langInfo = languageInfo[langCode] || { name: langCode.toUpperCase(), flag: '🌐' };
        const isActive = currentLanguage === langCode;
        
        return (
          <React.Fragment key={langCode}>
            {index > 0 && <span className="text-gray-500 text-xs">/</span>}
            <button
              onClick={() => handleLanguageToggle(langCode)}
              data-testid={`lang-btn-${langCode}`}
              className={`
                px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-amber-500/90 text-black shadow-sm' 
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }
              `}
              title={langInfo.fullName}
            >
              <span className="mr-1">{langInfo.flag}</span>
              {langInfo.name}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
